import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import DailyBanner from "@/components/DailyBanner";
import { ArrowUpRight, Zap, Users, Target, Trophy } from "lucide-react";

const HERO_BG = "https://images.unsplash.com/flagged/photo-1579451443170-44b3963c3341?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwzfHxuZW9uJTIwY3liZXJwdW5rJTIwZmFjZXxlbnwwfHx8fDE3ODI0MDgwODR8MA&ixlib=rb-4.1.0&q=85";

export default function Landing() {
  const [stats, setStats] = useState({ challenges: 0, votes: 0, users: 0, global_accuracy: 0 });
  const [featured, setFeatured] = useState(null);

  useEffect(() => {
    api.get("/stats/global").then((r) => setStats(r.data)).catch(() => {});
    api.get("/challenges").then((r) => setFeatured(r.data[0])).catch(() => {});
  }, []);

  return (
    <div data-testid="landing-page">
      {/* HERO */}
      <section className="relative overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(5,5,5,0.7) 0%, rgba(5,5,5,0.95) 70%, #050505 100%)" }} />
          <div className="scan-beam" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 sm:px-8 md:px-12 pt-20 pb-28 md:pt-32 md:pb-40">
          <div className="label-mono mb-6 slide-up" data-testid="hero-eyebrow">
            <span style={{ color: "var(--ai)" }}>● </span>Episode 001 — Live now
          </div>
          <h1 className="font-display font-black tracking-tighter uppercase text-5xl sm:text-6xl lg:text-8xl leading-[0.92] slide-up" style={{ animationDelay: "0.1s" }} data-testid="hero-headline">
            Can you still
            <br />
            <span style={{ color: "var(--text-dim)" }}>tell the </span>
            <span style={{ color: "var(--human)" }}>human</span>
            <br />
            <span style={{ color: "var(--text-dim)" }}>from the </span>
            <span style={{ color: "var(--ai)" }}>machine?</span>
          </h1>
          <p className="mt-8 max-w-xl text-lg leading-relaxed slide-up" style={{ color: "var(--text-dim)", animationDelay: "0.2s" }} data-testid="hero-sub">
            One slogan generated €1M. The other was written by AI in 30 seconds. Vote before the reveal — and watch the world close the gap, one challenge at a time.
          </p>
          <div className="mt-10 flex flex-wrap gap-4 slide-up" style={{ animationDelay: "0.3s" }}>
            <Link to="/challenges" className="btn btn-primary" data-testid="hero-play-button">
              Play the challenge <ArrowUpRight size={14} />
            </Link>
            <Link to="/leaderboard" className="btn btn-secondary" data-testid="hero-leaderboard-button">
              See leaderboard
            </Link>
          </div>
        </div>

        {/* Stats strip */}
        <div className="relative border-t border-b" style={{ borderColor: "var(--border)" }} data-testid="hero-stats">
          <div className="max-w-7xl mx-auto px-6 sm:px-8 md:px-12 py-6 grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { icon: Zap, label: "Live episodes", value: stats.challenges },
              { icon: Users, label: "Players", value: stats.users },
              { icon: Target, label: "Total votes", value: stats.votes },
              { icon: Trophy, label: "Global accuracy", value: `${stats.global_accuracy}%` },
            ].map((s, i) => (
              <div key={i} className="flex items-center gap-3 count-up" style={{ animationDelay: `${0.4 + i * 0.08}s` }}>
                <s.icon size={18} style={{ color: "var(--text-dim)" }} />
                <div>
                  <div className="font-display font-bold text-2xl">{s.value}</div>
                  <div className="label-mono mt-1">{s.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Challenge */}
      {featured && (
        <section className="max-w-7xl mx-auto px-6 sm:px-8 md:px-12 py-20" data-testid="featured-section">
          <div className="mb-12">
            <DailyBanner />
          </div>
          <div className="flex items-end justify-between mb-8">
            <div>
              <div className="label-mono mb-3">Featured · Episode of the Week</div>
              <h2 className="font-display font-bold text-3xl md:text-5xl tracking-tight">
                {featured.title}
              </h2>
            </div>
            <Link to={`/challenges/${featured.id}`} className="btn btn-primary hidden md:inline-flex" data-testid="featured-vote-button">
              Vote now <ArrowUpRight size={14} />
            </Link>
          </div>
          {featured.hook && (
            <p className="text-lg max-w-3xl mb-10" style={{ color: "var(--text-dim)" }}>
              {featured.hook}
            </p>
          )}
          <div className="split-grid">
            {[featured.option_a, featured.option_b].map((opt) => (
              <div key={opt.label} className="opt-panel locked">
                <span className="opt-letter">{opt.label}</span>
                <div className="label-mono mb-4">Option {opt.label}</div>
                <div className="text-xl md:text-2xl leading-snug font-medium pr-12">
                  {opt.content}
                </div>
              </div>
            ))}
          </div>
          <Link to={`/challenges/${featured.id}`} className="btn btn-primary mt-6 md:hidden" data-testid="featured-vote-button-mobile">
            Vote now <ArrowUpRight size={14} />
          </Link>
        </section>
      )}

      {/* How it works */}
      <section className="max-w-7xl mx-auto px-6 sm:px-8 md:px-12 py-20" data-testid="how-it-works">
        <div className="label-mono mb-3">The Formula</div>
        <h2 className="font-display font-bold text-3xl md:text-5xl tracking-tight mb-12">Five steps. Endless episodes.</h2>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
          {[
            ["01", "Show", "Two options. A and B. Side by side."],
            ["02", "Vote", "Pick the one you think the AI made."],
            ["03", "Reveal", "Watch the reveal animation."],
            ["04", "Explain", "Learn why one outperformed the other."],
            ["05", "Submit", "Send your own to be featured next."],
          ].map(([n, h, d], i) => (
            <div key={i} className="card p-6 slide-up" style={{ animationDelay: `${i * 0.08}s` }}>
              <div className="label-mono mb-4">{n}</div>
              <div className="font-display font-bold text-lg mb-2">{h}</div>
              <div className="text-sm" style={{ color: "var(--text-dim)" }}>{d}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Monetization */}
      <section className="max-w-7xl mx-auto px-6 sm:px-8 md:px-12 py-20" data-testid="partners-section">
        <div className="grid md:grid-cols-2 gap-6">
          <div className="card p-10 relative overflow-hidden">
            <div className="label-mono mb-3" style={{ color: "var(--ai)" }}>Templates · From $19</div>
            <h3 className="font-display font-bold text-3xl md:text-4xl tracking-tight mb-4">
              Steal the human plays that beat AI.
            </h3>
            <p style={{ color: "var(--text-dim)" }} className="mb-6">
              Cold-email openers, pitch-deck swipe files, slogan vaults — the source material behind the human winners.
            </p>
            <Link to="/store" className="btn btn-primary" data-testid="landing-store-cta">
              Browse store <ArrowUpRight size={14} />
            </Link>
          </div>
          <div className="card p-10 relative overflow-hidden">
            <div className="label-mono mb-3" style={{ color: "#fcd34d" }}>For Brands & Educators</div>
            <h3 className="font-display font-bold text-3xl md:text-4xl tracking-tight mb-4">
              Sponsor a challenge.
            </h3>
            <p style={{ color: "var(--text-dim)" }} className="mb-6">
              Put your brand in front of CMOs and founders who actively compare AI to human craft. Every episode is a conversation starter.
            </p>
            <a href="mailto:partners@aivshuman.host?subject=Sponsor%20an%20episode" className="btn btn-secondary" data-testid="landing-sponsor-cta">
              Talk to us <ArrowUpRight size={14} />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
